/** @jsxImportSource @emotion/react */
import React from "react";
import Form from "react-bootstrap/Form";
import TextField from "../../../Utils/FormInputs/TextField";
import SelectInput from "../../../Utils/FormInputs/SelectInput";
import { ResizableTextarea } from "../../../Utils/FormInputs/Textarea";
import Label from "../../../Utils/FormInputs/Label";
import {
    PNCFormGrid,
    PNCCheckboxGrid, PNCCheckboxItem,
    PNCToggle, PNCToggleSwitch, PNCToggleLabel,
    PNCPrimaryBtn, PNCSecondaryBtn, PNCButtonRow,
    PNCAlertContainer, PNCAlertCard, PNCAlertContent,
    PNCAlertTitle, PNCAlertDetail, PNCAlertAction,
    PNCSectionDivider, PNCSectionLine, PNCSectionLabel,
    WeightTrendCard, WeightTrendHeader, WeightTrendBody,
    WeightBar, WeightBarLabel, WeightBarTrack, WeightBarFill, WeightBarValue,
    PNCFormTitle, PNCFormBadge, PNCHint,
    PNCSummaryStrip, PNCSummaryItem, PNCSummaryLabel, PNCSummaryValue,
} from "./PostnatalCareStyles";

/* ═══════════════════════════════════════════════════════════════
   PostnatalNewbornForm — Newborn PNC Assessment Form (Task 16)
   ═══════════════════════════════════════════════════════════════ */

var toSelectOptions = function (items) {
    return items.map(function (m) { return { value: m.value, text: m.label }; });
};

/* ─── Newborn Info Banner ─── */
var NewbornInfoBanner = function (props) {
    var newborn = props.newborn;
    var daysOfLife = props.daysOfLife;
    if (!newborn) return null;

    return (
        <PNCSummaryStrip>
            <PNCSummaryItem>
                <PNCSummaryLabel>Sex</PNCSummaryLabel>
                <PNCSummaryValue>{newborn.sex === "male" ? "Male" : newborn.sex === "female" ? "Female" : newborn.sex}</PNCSummaryValue>
            </PNCSummaryItem>
            <PNCSummaryItem>
                <PNCSummaryLabel>Birth Weight</PNCSummaryLabel>
                <PNCSummaryValue>{newborn.birth_weight_grams}g</PNCSummaryValue>
            </PNCSummaryItem>
            <PNCSummaryItem>
                <PNCSummaryLabel>APGAR</PNCSummaryLabel>
                <PNCSummaryValue>{newborn.apgar_1min}/{newborn.apgar_5min}</PNCSummaryValue>
            </PNCSummaryItem>
            <PNCSummaryItem>
                <PNCSummaryLabel>Days of Life</PNCSummaryLabel>
                <PNCSummaryValue>{daysOfLife !== null ? daysOfLife : "—"}</PNCSummaryValue>
            </PNCSummaryItem>
            <PNCSummaryItem>
                <PNCSummaryLabel>Outcome</PNCSummaryLabel>
                <PNCSummaryValue>{newborn.outcome === "alive" ? "Alive" : newborn.outcome}</PNCSummaryValue>
            </PNCSummaryItem>
            {newborn.nevirapine_given && (
                <PNCSummaryItem warn>
                    <PNCSummaryLabel>HIV-Exposed</PNCSummaryLabel>
                    <PNCSummaryValue warn>Yes</PNCSummaryValue>
                </PNCSummaryItem>
            )}
        </PNCSummaryStrip>
    );
};

/* ─── Weight Trend Chart ─── */
var WeightTrendChart = function (props) {
    var weightTrend = props.weightTrend || [];
    if (weightTrend.length === 0) return null;

    var maxWeight = Math.max.apply(null, weightTrend.map(function (w) { return w.weight; }));
    var maxW = Math.max(maxWeight * 1.2, 4000);
    var birthWeight = weightTrend[0] ? weightTrend[0].weight : 0;

    return (
        <WeightTrendCard>
            <WeightTrendHeader>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <PNCFormTitle>Weight Trend</PNCFormTitle>
                </div>
                <PNCFormBadge variant="success">
                    Birth: {birthWeight}g
                </PNCFormBadge>
            </WeightTrendHeader>
            <WeightTrendBody>
                {weightTrend.map(function (entry, idx) {
                    var pct = (entry.weight / maxW) * 100;
                    var lossPercent = birthWeight > 0 ? ((entry.weight - birthWeight) / birthWeight) * 100 : 0;
                    var isDanger = lossPercent < -10;
                    var isWarning = lossPercent < -7 && !isDanger;
                    return (
                        <WeightBar key={idx} maxWeight={maxW} weight={entry.weight}>
                            <WeightBarLabel>{entry.visit}</WeightBarLabel>
                            <WeightBarTrack>
                                <WeightBarFill percent={pct} danger={isDanger} warning={isWarning} />
                            </WeightBarTrack>
                            <WeightBarValue danger={isDanger} warning={isWarning}>
                                {entry.weight}g
                                {idx > 0 && (
                                    <span style={{ fontSize: "0.68rem", marginLeft: "4px" }}>
                                        ({lossPercent >= 0 ? "+" : ""}{lossPercent.toFixed(1)}%)
                                    </span>
                                )}
                            </WeightBarValue>
                        </WeightBar>
                    );
                })}
            </WeightTrendBody>
        </WeightTrendCard>
    );
};

/* ═══ MAIN COMPONENT ═══ */
export var PostnatalNewbornForm = function (props) {
    var nb = props.newbornPnc;

    var newborn = nb.newborn;
    var form = nb.form;
    var handleChange = nb.handleChange;
    var toggleImmunization = nb.toggleImmunization;
    var toggleDangerSign = nb.toggleDangerSign;
    var alerts = nb.alerts;
    var pncVisits = nb.pncVisits;
    var editingVisit = nb.editingVisit;
    var saving = nb.saving;
    var savePncVisit = nb.savePncVisit;
    var resetForm = nb.resetForm;
    var hivExposed = nb.hivExposed;
    var weightTrend = nb.weightTrend;
    var daysOfLife = nb.daysOfLife;

    /* Build select options */
    var visitOptions = [
        { value: "1", text: "PNC 1 — Within 24 hours" },
        { value: "2", text: "PNC 2 — Day 3" },
        { value: "3", text: "PNC 3 — Day 7" },
        { value: "4", text: "PNC 4 — 6 Weeks" },
    ];
    var feedingOptions = toSelectOptions(nb.FEEDING_STATUS);
    var cordOptions = toSelectOptions(nb.CORD_STATUS);
    var jaundiceOptions = toSelectOptions(nb.JAUNDICE_LEVELS);
    var breathingOptions = toSelectOptions(nb.BREATHING_STATUS);
    var activityOptions = toSelectOptions(nb.ACTIVITY_STATUS);
    var dbsOptions = [
        { value: "negative", text: "Negative (HIV DNA not detected)" },
        { value: "positive", text: "Positive (HIV DNA detected)" },
        { value: "indeterminate", text: "Indeterminate — Repeat needed" },
    ];

    if (!newborn) {
        return (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <h3 style={{ margin: "0 0 8px", fontWeight: 700, color: "#444" }}>No Newborn Record Found</h3>
                <p style={{ color: "#888", fontSize: "0.85rem" }}>
                    A newborn record must be created from the Delivery & Birth Record page before PNC can begin.
                </p>
            </div>
        );
    }

    return (
        <div>
            {/* ─── Newborn Info ─── */}
            <NewbornInfoBanner newborn={newborn} daysOfLife={daysOfLife} />

            {/* ─── Alerts ─── */}
            {alerts.length > 0 && (
                <PNCAlertContainer>
                    {alerts.map(function (alert, idx) {
                        return (
                            <PNCAlertCard key={idx} severity={alert.type}>
                                <PNCAlertContent>
                                    <PNCAlertTitle>{alert.title}</PNCAlertTitle>
                                    <PNCAlertDetail>{alert.detail}</PNCAlertDetail>
                                    {alert.action && <PNCAlertAction>{alert.action}</PNCAlertAction>}
                                </PNCAlertContent>
                            </PNCAlertCard>
                        );
                    })}
                </PNCAlertContainer>
            )}

            {/* ─── Weight Trend ─── */}
            {/* <WeightTrendChart weightTrend={weightTrend} /> */}

            {/* ─── Header: Edit / New ─── */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
                <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#333" }}>
                    {editingVisit ? "Edit Newborn PNC Visit " + editingVisit.pnc_visit_number : "New Newborn PNC Assessment"}
                </h3>
                {editingVisit && (
                    <PNCSecondaryBtn onClick={resetForm} style={{ padding: "5px 12px", fontSize: "0.78rem" }}>
                        Cancel Edit
                    </PNCSecondaryBtn>
                )}
            </div>

            {/* ─── Visit Number & Date ─── */}
            <PNCFormGrid cols={2}>
                <Form.Group>
                    <Label name="PNC Visit Number" type />
                    <SelectInput
                        name="pnc_visit_number"
                        value={form.pnc_visit_number}
                        onChange={handleChange}
                        options={visitOptions}
                        defaultOption="— Select —"
                    />
                </Form.Group>
                <Form.Group>
                    <Label name="Visit Date" type />
                    <TextField type="date" name="visit_date" value={form.visit_date} onChange={handleChange} />
                </Form.Group>
            </PNCFormGrid>

            {/* ─── Growth & Temperature ─── */}
            <PNCSectionDivider>
                <PNCSectionLine />
                <PNCSectionLabel>Growth & Temperature</PNCSectionLabel>
                <PNCSectionLine />
            </PNCSectionDivider>

            <PNCFormGrid cols={2}>
                <Form.Group>
                    <Label name="Weight (grams)" type />
                    <TextField type="number" name="weight_grams" value={form.weight_grams}
                        onChange={handleChange} placeholder="3200" min="500" max="7000" />
                    {newborn && form.weight_grams && (
                        <PNCHint warn={parseInt(form.weight_grams) < newborn.birth_weight_grams * 0.9}>
                            Birth weight: {newborn.birth_weight_grams}g
                            {parseInt(form.weight_grams) > 0 && (
                                " | Change: " +
                                (((parseInt(form.weight_grams) - newborn.birth_weight_grams) / newborn.birth_weight_grams) * 100).toFixed(1) + "%"
                            )}
                        </PNCHint>
                    )}
                </Form.Group>
                <Form.Group>
                    <Label name="Temperature (°C)" />
                    <TextField type="number" name="temperature" value={form.temperature}
                        onChange={handleChange} placeholder="36.5" step="0.1" min="34" max="42" />
                    {parseFloat(form.temperature) >= 37.5 && (
                        <PNCHint warn>Fever — investigate neonatal sepsis</PNCHint>
                    )}
                    {parseFloat(form.temperature) > 0 && parseFloat(form.temperature) < 35.5 && (
                        <PNCHint warn>Hypothermia — kangaroo care, check for sepsis</PNCHint>
                    )}
                </Form.Group>
            </PNCFormGrid>

            {/* ─── Physical Examination ─── */}
            <PNCSectionDivider>
                <PNCSectionLine />
                <PNCSectionLabel>Physical Examination</PNCSectionLabel>
                <PNCSectionLine />
            </PNCSectionDivider>

            <PNCFormGrid cols={3}>
                <Form.Group>
                    <Label name="Feeding Status" />
                    <SelectInput
                        name="feeding_status"
                        value={form.feeding_status}
                        onChange={handleChange}
                        options={feedingOptions}
                        defaultOption="— Select —"
                    />
                </Form.Group>
                <Form.Group>
                    <Label name="Cord Status" />
                    <SelectInput
                        name="cord_status"
                        value={form.cord_status}
                        onChange={handleChange}
                        options={cordOptions}
                        defaultOption="— Select —"
                    />
                    {form.cord_status === "infected" && (
                        <PNCHint warn>Omphalitis — antibiotics needed</PNCHint>
                    )}
                </Form.Group>
                <Form.Group>
                    <Label name="Jaundice" />
                    <SelectInput
                        name="jaundice"
                        value={form.jaundice}
                        onChange={handleChange}
                        options={jaundiceOptions}
                        defaultOption="— Select —"
                    />
                    {form.jaundice === "severe" && (
                        <PNCHint warn>SEVERE — URGENT REFERRAL for phototherapy</PNCHint>
                    )}
                </Form.Group>
                <Form.Group>
                    <Label name="Breathing" />
                    <SelectInput
                        name="breathing"
                        value={form.breathing}
                        onChange={handleChange}
                        options={breathingOptions}
                        defaultOption="— Select —"
                    />
                </Form.Group>
                <Form.Group>
                    <Label name="Activity / Tone" />
                    <SelectInput
                        name="activity"
                        value={form.activity}
                        onChange={handleChange}
                        options={activityOptions}
                        defaultOption="— Select —"
                    />
                </Form.Group>
            </PNCFormGrid>

            {/* ─── Immunizations ─── */}
            <PNCSectionDivider>
                <PNCSectionLine />
                <PNCSectionLabel>Immunizations</PNCSectionLabel>
                <PNCSectionLine />
            </PNCSectionDivider>

            <div style={{
                padding: "10px 14px",
                borderRadius: "8px",
                background: "#e8f5e9",
                border: "1px solid #c8e6c9",
                marginBottom: "14px",
                fontSize: "0.8rem",
                color: "#2e7d32",
            }}>
                Birth vaccines (BCG, OPV-0, Hep B) should be given within 24 hours. 6-week vaccines at PNC 4.
            </div>
            <PNCCheckboxGrid cols={2}>
                {nb.IMMUNIZATION_OPTIONS.map(function (imm) {
                    var checked = form.immunizations_given.indexOf(imm.value) >= 0;
                    var givenAtBirth = newborn && (
                        (imm.value === "bcg" && newborn.bcg_given) ||
                        (imm.value === "opv0" && newborn.opv0_given) ||
                        (imm.value === "hep_b_birth" && newborn.hep_b_birth_dose)
                    );
                    return (
                        <PNCCheckboxItem key={imm.value} checked={checked || givenAtBirth}
                            onClick={function () { if (!givenAtBirth) toggleImmunization(imm.value); }}>
                            <input type="checkbox" checked={checked || givenAtBirth} readOnly
                                style={{ width: "16px", height: "16px", accentColor: "#2e7d32" }} />
                            <div>
                                <span style={{ fontWeight: checked || givenAtBirth ? 600 : 400 }}>{imm.label}</span>
                                <div style={{ fontSize: "0.7rem", color: "#888", marginTop: "2px" }}>
                                    Schedule: {imm.scheduleAge}
                                    {givenAtBirth && " — Given at birth"}
                                </div>
                            </div>
                        </PNCCheckboxItem>
                    );
                })}
            </PNCCheckboxGrid>

            {/* ─── Danger Signs ─── */}
            <PNCSectionDivider>
                <PNCSectionLine />
                <PNCSectionLabel>Neonatal Danger Signs</PNCSectionLabel>
                <PNCSectionLine />
            </PNCSectionDivider>

            <div style={{
                padding: "10px 14px",
                borderRadius: "8px",
                background: "#fce4ec",
                border: "1px solid #f8bbd0",
                marginBottom: "14px",
                fontSize: "0.8rem",
                color: "#c62828",
            }}>
                Any danger sign requires immediate assessment and may need urgent referral.
            </div>
            <PNCCheckboxGrid cols={2}>
                {nb.NEWBORN_DANGER_SIGNS.map(function (sign) {
                    var checked = form.danger_signs.indexOf(sign.value) >= 0;
                    return (
                        <PNCCheckboxItem key={sign.value} checked={checked} danger={true}
                            onClick={function () { toggleDangerSign(sign.value); }}>
                            <input type="checkbox" checked={checked} readOnly
                                style={{ width: "16px", height: "16px", accentColor: "#c62828" }} />
                            <span>{sign.label}</span>
                        </PNCCheckboxItem>
                    );
                })}
            </PNCCheckboxGrid>

            {/* ─── HIV-Exposed Infant Follow-up (conditional) ─── */}
            {hivExposed && (
                <div>
                    <PNCSectionDivider>
                        <PNCSectionLine />
                        <PNCSectionLabel>HIV-Exposed Infant Follow-up</PNCSectionLabel>
                        <PNCSectionLine />
                    </PNCSectionDivider>

                    <div style={{
                        padding: "10px 14px",
                        borderRadius: "8px",
                        background: "#f3e5f5",
                        border: "1px solid #ce93d8",
                        marginBottom: "14px",
                        fontSize: "0.8rem",
                        color: "#6a1b9a",
                    }}>
                        This baby is HIV-exposed. Ensure: daily nevirapine prophylaxis, CTX from 6 weeks,
                        and DBS collection at 6 weeks for early infant diagnosis (EID).
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <PNCToggle checked={form.nevirapine_continued}
                            onClick={function () {
                                handleChange({ target: { name: "nevirapine_continued", type: "checkbox", checked: !form.nevirapine_continued } });
                            }}>
                            <PNCToggleSwitch checked={form.nevirapine_continued} />
                            <PNCToggleLabel checked={form.nevirapine_continued}>
                                Nevirapine prophylaxis continued (daily)
                            </PNCToggleLabel>
                        </PNCToggle>

                        <PNCToggle checked={form.ctx_started}
                            onClick={function () {
                                handleChange({ target: { name: "ctx_started", type: "checkbox", checked: !form.ctx_started } });
                            }}>
                            <PNCToggleSwitch checked={form.ctx_started} />
                            <PNCToggleLabel checked={form.ctx_started}>
                                Cotrimoxazole (CTX) prophylaxis started (from 6 weeks)
                            </PNCToggleLabel>
                        </PNCToggle>

                        <PNCToggle checked={form.dbs_collected}
                            onClick={function () {
                                handleChange({ target: { name: "dbs_collected", type: "checkbox", checked: !form.dbs_collected } });
                            }}>
                            <PNCToggleSwitch checked={form.dbs_collected} />
                            <PNCToggleLabel checked={form.dbs_collected}>
                                DBS (Dried Blood Spot) collected for HIV PCR (at 6 weeks)
                            </PNCToggleLabel>
                        </PNCToggle>
                    </div>

                    {form.dbs_collected && (
                        <Form.Group style={{ marginTop: "12px" }}>
                            <Label name="DBS Result" />
                            <SelectInput
                                name="dbs_result"
                                value={form.dbs_result}
                                onChange={handleChange}
                                options={dbsOptions}
                                defaultOption="— Pending / Not yet received —"
                            />
                            {form.dbs_result === "positive" && (
                                <PNCHint warn>
                                    POSITIVE: Start ART immediately. Refer to paediatric HIV clinic.
                                </PNCHint>
                            )}
                        </Form.Group>
                    )}
                </div>
            )}

            {/* ─── Clinical Notes ─── */}
            <PNCSectionDivider>
                <PNCSectionLine />
                <PNCSectionLabel>Clinical Notes</PNCSectionLabel>
                <PNCSectionLine />
            </PNCSectionDivider>

            <Form.Group>
                <Label name="Notes / Plan" />
                <ResizableTextarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Assessment findings, feeding support, follow-up plan..."
                />
            </Form.Group>

            {/* ─── Save Buttons ─── */}
            <PNCButtonRow>
                {editingVisit && (
                    <PNCSecondaryBtn onClick={resetForm}>Cancel Edit</PNCSecondaryBtn>
                )}
                <button className="btn sms-info-btn" disabled={saving} onClick={savePncVisit}>
                    {saving ? "Saving..." : editingVisit ? "Update Newborn PNC" : "Save Newborn PNC"}
                </button>
            </PNCButtonRow>

        </div>
    );
};
